import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';

const ResetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [matchError, setMatchError] = useState<string | null>(null);
    const [isLocalSubmitting, setIsLocalSubmitting] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const resetPassword = trpc.user.resetPassword.useMutation({
        onSuccess: (data) => {
            setIsSuccess(true);
            setIsLocalSubmitting(false);
            toast.success(data.message);
            setTimeout(() => navigate('/login'), 3000);
        },
        onError: (error) => {
            setIsLocalSubmitting(false);
            toast.error(error.message);
        },
    });

    // Real-time password validation
    const validatePassword = (pass: string) => {
        if (!pass) return null;
        if (pass.length < 6) return "Password must be at least 6 characters";
        return null;
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setNewPassword(val);
        setPasswordError(validatePassword(val));

        // Also re-check match if confirm field is filled
        if (confirmPassword && val !== confirmPassword) {
            setMatchError("Passwords do not match");
        } else if (confirmPassword) {
            setMatchError(null);
        }
    };

    const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setConfirmPassword(val);
        if (val && val !== newPassword) {
            setMatchError("Passwords do not match");
        } else {
            setMatchError(null);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!token) {
            toast.error('Invalid reset token');
            return;
        }

        const passErr = validatePassword(newPassword);
        if (passErr) {
            setPasswordError(passErr);
            toast.error(passErr);
            return;
        }

        if (newPassword !== confirmPassword) {
            setMatchError("Passwords do not match");
            toast.error("Passwords do not match");
            return;
        }

        setIsLocalSubmitting(true);
        resetPassword.mutate({ token, newPassword });
    };

    if (!token) {
        return (
            <div className="container mx-auto flex items-center justify-center min-h-[80vh] px-4 animate-fade-in">
                <Card className="w-full max-w-md glass border-red-500/20">
                    <CardContent className="pt-6 text-center">
                        <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold text-red-500 mb-2">Invalid Link</h3>
                        <p className="text-muted-foreground mb-4">
                            This password reset link is invalid or missing. Please try requesting a new one.
                        </p>
                        <Button asChild variant="outline">
                            <Link to="/forgot-password">Request New Link</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const isLoading = resetPassword.isLoading || isLocalSubmitting;

    return (
        <div className="container mx-auto flex items-center justify-center min-h-[80vh] px-4 animate-fade-in">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "circOut" }}
                className="w-full max-w-md"
            >
                <Card className="glass shadow-2xl border-foreground/10 overflow-hidden relative">
                    {/* Ambient Background Glow */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-32 bg-primary/20 blur-[60px] rounded-full pointer-events-none" />

                    <CardHeader className="space-y-1 relative z-10">
                        <CardTitle className="text-2xl font-bold text-center bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
                            Reset Password
                        </CardTitle>
                        <CardDescription className="text-center">
                            Create a strong password to secure your account.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="relative z-10">
                        {isSuccess ? (
                            <div className="text-center py-6 animate-fade-in">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"
                                >
                                    <CheckCircle className="w-8 h-8 text-green-500" />
                                </motion.div>
                                <h3 className="text-xl font-semibold mb-2">Password Reset Successful!</h3>
                                <p className="text-muted-foreground mb-4">
                                    Your password has been updated. Redirecting to login...
                                </p>
                                <Button className="w-full" asChild>
                                    <Link to="/login">Login Now</Link>
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="newPassword">New Password</Label>
                                    <div className="relative group">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="newPassword"
                                            type={showNewPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={newPassword}
                                            onChange={handlePasswordChange}
                                            disabled={isLoading}
                                            className={`pl-9 pr-10 bg-background/50 transition-all duration-300 focus:ring-2 focus:ring-primary/20 ${passwordError ? 'border-red-500/50 focus:ring-red-500/20' : ''}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                                            tabIndex={-1}
                                        >
                                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {passwordError && (
                                        <p className="text-xs text-red-500 animate-slide-up">
                                            {passwordError}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                                    <div className="relative group">
                                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                                        <Input
                                            id="confirmPassword"
                                            type={showConfirmPassword ? "text" : "password"}
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={handleConfirmChange}
                                            disabled={isLoading}
                                            className={`pl-9 pr-10 bg-background/50 transition-all duration-300 focus:ring-2 focus:ring-primary/20 ${matchError ? 'border-red-500/50 focus:ring-red-500/20' : ''}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                                            tabIndex={-1}
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    {matchError && (
                                        <p className="text-xs text-red-500 animate-slide-up">
                                            {matchError}
                                        </p>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full relative overflow-hidden bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-primary/25 disabled:opacity-80 disabled:cursor-not-allowed group"
                                    disabled={isLoading || !!passwordError || !!matchError || !newPassword || !confirmPassword}
                                >
                                    <span className={`flex items-center justify-center gap-2 transition-all duration-300 ${isLoading ? 'opacity-100' : 'opacity-100'}`}>
                                        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {isLoading ? "Resetting Password..." : "Reset Password"}
                                    </span>
                                    {!isLoading && (
                                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-md" />
                                    )}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
};

export default ResetPasswordPage;
